import { supabase } from "@/integrations/supabase/client";
import { Browser } from "@capacitor/browser";
import { logger } from "@/lib/logger";

const NATIVE_PROTOCOL = "com.birchwrite.fiction:";
const NATIVE_HOST = "auth";
const NATIVE_PATH = "/callback";
const WEB_CALLBACK_PATHS = new Set(["/auth", "/auth/callback"]);

const inFlightCallbacks = new Map<string, Promise<boolean>>();
const completedCallbacks = new Set<string>();
const MAX_COMPLETED_CALLBACKS = 32;

function isAcceptedCallback(parsed: URL): boolean {
  const isNative =
    parsed.protocol === NATIVE_PROTOCOL &&
    parsed.hostname === NATIVE_HOST &&
    parsed.pathname === NATIVE_PATH;

  let isSameOriginWebCallback = false;

  if (typeof window !== "undefined") {
    isSameOriginWebCallback =
      parsed.origin === window.location.origin &&
      WEB_CALLBACK_PATHS.has(parsed.pathname);
  }

  return isNative || isSameOriginWebCallback;
}

function getCallbackParameters(parsed: URL): URLSearchParams {
  const combined = new URLSearchParams(parsed.search);

  if (parsed.hash.length > 1) {
    const hashParams = new URLSearchParams(parsed.hash.slice(1));
    hashParams.forEach((value, key) => {
      if (!combined.has(key)) {
        combined.set(key, value);
      }
    });
  }

  return combined;
}

function getCallbackIdentity(params: URLSearchParams): string | null {
  const code = params.get("code");
  if (code) return `code:${code}`;

  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");

  if (accessToken && refreshToken) {
    return `tokens:${accessToken}:${refreshToken}`;
  }

  return null;
}

function rememberCompleted(identity: string): void {
  completedCallbacks.add(identity);

  while (completedCallbacks.size > MAX_COMPLETED_CALLBACKS) {
    const oldest = completedCallbacks.values().next().value as
      | string
      | undefined;

    if (!oldest) break;
    completedCallbacks.delete(oldest);
  }
}

async function closeAuthBrowser(): Promise<void> {
  try {
    await Browser.close();
  } catch {
    // The callback may have arrived without an active Capacitor browser.
  }
}

async function processAcceptedCallback(
  parsed: URL,
  params: URLSearchParams,
  onAuthenticated?: () => void
): Promise<boolean> {
  const providerError =
    params.get("error_description") ||
    params.get("error") ||
    params.get("error_code");

  if (providerError) {
    console.error("[DeepLink] Authentication provider returned an error");
    return false;
  }

  const code = params.get("code");

  if (code) {
    logger.log("[DeepLink] Exchanging PKCE authorization code");

    const { data, error } = await supabase.auth.exchangeCodeForSession(code);

    if (error) {
      console.error("[DeepLink] PKCE exchange failed:", error.message);
      return false;
    }

    if (!data.session) {
      console.error("[DeepLink] PKCE exchange returned no session");
      return false;
    }

    onAuthenticated?.();
    return true;
  }

  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");

  if (accessToken && refreshToken) {
    logger.log("[DeepLink] Restoring session from callback tokens");

    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      console.error("[DeepLink] Token session restoration failed:", error.message);
      return false;
    }

    if (!data.session) {
      console.error("[DeepLink] Token restoration returned no session");
      return false;
    }

    onAuthenticated?.();
    return true;
  }

  console.warn("[DeepLink] Accepted callback contained no auth credentials");
  return false;
}

export async function handleAuthDeepLink(
  url: string,
  onAuthenticated?: () => void
): Promise<boolean> {
  if (!url) return false;

  let parsed: URL;

  try {
    parsed = new URL(url);
  } catch {
    console.warn("[DeepLink] Ignoring malformed callback URL");
    return false;
  }

  if (!isAcceptedCallback(parsed)) {
    return false;
  }

  const params = getCallbackParameters(parsed);
  const identity = getCallbackIdentity(params);

  if (!identity) {
    await closeAuthBrowser();
    return processAcceptedCallback(parsed, params, onAuthenticated);
  }

  if (completedCallbacks.has(identity)) {
    logger.log("[DeepLink] Ignoring duplicate completed callback");
    await closeAuthBrowser();
    onAuthenticated?.();
    return true;
  }

  const existing = inFlightCallbacks.get(identity);
  if (existing) {
    logger.log("[DeepLink] Reusing in-flight callback exchange");
    return existing;
  }

  const operation = (async () => {
    try {
      const succeeded = await processAcceptedCallback(
        parsed,
        params,
        onAuthenticated
      );

      if (succeeded) {
        rememberCompleted(identity);
      }

      return succeeded;
    } catch (error) {
      console.error("[DeepLink] Unexpected callback failure:", error);
      return false;
    } finally {
      inFlightCallbacks.delete(identity);
      await closeAuthBrowser();
    }
  })();

  inFlightCallbacks.set(identity, operation);
  return operation;
}
